/**
 * switchApplyResult — what a switch CLI result MEANS, in words an operator can act on.
 *
 * WHY THIS EXISTS (S3, 2026-09-14)
 * The port panels said "Applied and saved to startup-config." whenever `ok` was true. `ok` only
 * means "no line was refused"; whether the persist happened is a separate field (`saved`, from
 * services/switch_cli._save_outcome: true only when the switch printed its flash-write success
 * marker). A green "saved" over an unsaved change is the same false-green class as the
 * 2026-09-09 rejected-VLAN bug: the change silently disappears on the next reboot.
 *
 * Pure functions, no React, so the wording rules are pinned by `npm run test:unit`.
 * Field shapes mirror services/switch_cli.apply_cli and services/switch_cli_relay.safe_view.
 */

/** How the controller reaches (or will reach) the switch -- switch_cli_relay.safe_view(). */
export interface SwitchCliAccess {
  mode: "direct" | "relay" | "unresolved";
  switch_host?: string | null;
  /** "auto" (derived) or "override" (the site's relay override decided). */
  source?: string | null;
  /** Human explanation of the decision, from the backend. */
  reason?: string | null;
  gateway?: {
    serial: string;
    name: string;
    role?: string | null;
    host: string;
    port: number;
    username?: string | null;
  } | null;
  /** Where the gateway credential came from (a path, never the value). Null on a preview. */
  credential_source?: string | null;
  error?: { code: string; message: string } | null;
}

/** The gateway a relayed session actually went through -- switch_cli._via_view(). No secret. */
export interface SwitchCliVia {
  gateway_serial: string | null;
  gateway_name: string | null;
  gateway_host: string | null;
  gateway_port: number;
  gateway_username: string | null;
}

/** One line the switch refused ("% Invalid input ..."), attributed when the echo allowed it. */
export interface SwitchCliRejection {
  index: number | null;
  command: string | null;
  error: string;
}

/** The subset of an apply result these helpers read. */
export interface SwitchApplyOutcome {
  ok: boolean;
  dry_run: boolean;
  error: string | null;
  /** true = switch confirmed the save; false = save was sent but not confirmed; null = no save. */
  saved?: boolean | null;
  /** true = some lines landed in running-config before a refusal (NOT saved). */
  partial?: boolean;
  rejected?: SwitchCliRejection[];
  /** relay | gateway | switch -- where a failed session broke. */
  stage?: string | null;
  via?: SwitchCliVia | null;
  access?: SwitchCliAccess | null;
}

/** healthy / warn / critical: operational state only (the status ramp), never decoration. */
export type OutcomeTone = "healthy" | "warn" | "critical";

export interface OutcomeMessage {
  tone: OutcomeTone;
  title: string;
  detail?: string;
}

/**
 * One line naming the access path, e.g. "Via gateway JH-GW-01 to 192.168.1.32".
 * Empty string when the backend sent no access (e.g. CLI writes disabled before resolution).
 */
export function describeAccess(access: SwitchCliAccess | null | undefined): string {
  if (!access) return "";
  if (access.mode === "relay") {
    const gw = access.gateway?.name || access.gateway?.serial || "the site gateway";
    return access.switch_host ? `Via gateway ${gw} to ${access.switch_host}` : `Via gateway ${gw}`;
  }
  if (access.mode === "direct") {
    return access.switch_host ? `Direct to ${access.switch_host}` : "Direct";
  }
  return `Cannot reach the switch yet: ${access.error?.message ?? "access path unresolved"}`;
}

/** Why a non-ok result failed, phrased by where it broke. */
function failureTitle(res: SwitchApplyOutcome): string {
  if (res.stage === "relay") return "Could not open a session through the site gateway";
  if (res.stage === "gateway") return "The gateway refused the relay session";
  if (res.partial) return "Partly applied — the switch refused a command";
  if (res.rejected && res.rejected.length > 0) return "The switch refused the change — nothing was applied";
  return "The change did not run";
}

/**
 * The message for a REAL apply (dry_run false) of a change that may include a save.
 *
 * saveRequested is what the operator asked for (the checkbox), because `saved: null` means two
 * different things: "no save in this command set" (fine, but the change is volatile) and "the
 * session never got that far" (only possible with ok false, handled first).
 */
export function describeApplyOutcome(
  res: SwitchApplyOutcome,
  opts: { saveRequested: boolean },
): OutcomeMessage {
  if (res.dry_run) return { tone: "healthy", title: "Preview only — nothing ran on the switch." };
  if (!res.ok) {
    return { tone: "critical", title: failureTitle(res), detail: res.error ?? undefined };
  }
  if (!opts.saveRequested) {
    return {
      tone: "warn",
      title: "Applied to running-config only",
      detail: "Not saved. The switch reverts this change on its next reboot unless you save it.",
    };
  }
  if (res.saved === true) return { tone: "healthy", title: "Applied and saved to startup-config." };
  return {
    tone: "warn",
    title: "Applied, but the save was not confirmed",
    detail:
      "The change is live. The switch did not confirm writing startup-config, so it may be " +
      "lost on the next reboot. Save to startup-config to retry.",
  };
}

/** The message for the standalone "Save to startup-config" action. */
export function describeSaveOutcome(res: SwitchApplyOutcome): OutcomeMessage {
  if (res.dry_run) return { tone: "healthy", title: "Preview only — nothing ran on the switch." };
  if (!res.ok) return { tone: "critical", title: failureTitle(res), detail: res.error ?? undefined };
  if (res.saved === true) return { tone: "healthy", title: "Saved to startup-config." };
  return {
    tone: "warn",
    title: "Save sent, but not confirmed",
    detail:
      "The session finished without the switch reporting a successful flash write. " +
      "Check `show startup-config` on the switch or try again.",
  };
}

/** True when a result leaves running-config ahead of startup-config (offer "Save now"). */
export function needsSave(res: SwitchApplyOutcome | null | undefined): boolean {
  return !!res && !res.dry_run && res.ok && res.saved !== true;
}

/**
 * The four outcomes the bulk port editor reports per switch (plus preview):
 *   saved          applied and the switch confirmed the flash write
 *   unsaved        applied to running-config, save not requested or not confirmed (offer Save)
 *   rejected       the switch refused a command line (show that line)
 *   relay_refused  Meridian could not open the session through the site gateway (plain words)
 *   failed         anything else (switch unreachable, timeout, ...): the backend's message
 */
export type ApplyResultKind = "preview" | "saved" | "unsaved" | "rejected" | "relay_refused" | "failed";

export function classifyApplyResult(res: SwitchApplyOutcome, opts: { saveRequested: boolean }): ApplyResultKind {
  if (res.dry_run) return "preview";
  if (res.ok) return opts.saveRequested && res.saved === true ? "saved" : "unsaved";
  if (res.rejected && res.rejected.length > 0) return "rejected";
  if (res.stage === "relay" || res.stage === "gateway") return "relay_refused";
  return "failed";
}

/**
 * Relay refusal codes (services/switch_cli_relay RelayUnavailable / _fail) in plain words. The
 * backend message names Vault paths and env vars -- right for the audit log and whoever fixes
 * it, noise for the operator deciding what to do next. Unknown codes fall back to the message.
 */
const RELAY_WORDS: Record<string, string> = {
  relay_credential_missing: "the gateway login for relaying isn't stored yet",
  relay_no_gateway: "this site has no gateway Meridian can relay through",
  relay_gateway_no_address: "Meridian does not know the gateway's address yet",
  relay_gateway_not_at_site: "the relay gateway chosen for this switch is not at its site",
  switch_lan_ip_unknown: "Meridian does not know the switch's address behind the gateway yet",
  switch_no_mgmt_ip: "the switch has no management IP",
  resolution_failed: "Meridian could not work out how to reach this switch",
};

/** Plain-words reason a relayed session was refused, e.g. "the gateway login for relaying isn't stored yet". */
export function relayRefusalReason(res: SwitchApplyOutcome): string {
  const code = res.access?.error?.code;
  if (code && RELAY_WORDS[code]) return RELAY_WORDS[code];
  if (res.stage === "gateway") return `the gateway refused the session${res.error ? `: ${res.error}` : ""}`;
  return res.error ?? "the session through the site gateway could not be opened";
}
